import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {openGuestStream, type StreamConnectionState} from '../api';
import type {SyncStreamEvent} from '../api/types';
import {useGuestSession} from '../session';

/**
 * One SSE connection for the whole page, fanned out to whoever subscribes.
 *
 * One connection, not one per screen: a browser allows a small number of
 * concurrent connections per origin, and a menu screen, a cart and a status
 * screen each holding their own stream would spend them on duplicate data. It
 * also means a screen transition does not tear down and re-establish the stream
 * on a connection that took two seconds to open.
 *
 * Subscribers get every frame and filter for themselves. The stream carries
 * exactly two kinds of thing — availability for `guest_qr`, and this table's own
 * order — so there is not enough traffic to justify routing by type.
 *
 * **Every frame is a hint to re-read.** Nothing here is authoritative, and
 * nothing about money is confirmed by a pushed frame.
 */

export type StreamListener = (event: SyncStreamEvent) => void;

export interface GuestStreamValue {
  connection: StreamConnectionState;
  /** Returns an unsubscribe function. Call it in a `useEffect` cleanup. */
  subscribe: (listener: StreamListener) => () => void;
  /**
   * `stream.reset` — the resume point fell out of the replay buffer, so every
   * cached snapshot is suspect and must be re-read. Increments on each reset;
   * put it in a dependency array to re-fetch.
   */
  resetNonce: number;
  /**
   * True when live updates are not arriving. Q14 renders honest staleness from
   * this rather than freezing a progress bar; nothing else should treat it as an
   * error, because offline is the supported state of this product.
   */
  stale: boolean;
}

const GuestStreamContext = createContext<GuestStreamValue | null>(null);

export function GuestStreamProvider({children}: {children: ReactNode}) {
  const {status, client} = useGuestSession();
  const [connection, setConnection] = useState<StreamConnectionState>('closed');
  const [resetNonce, setResetNonce] = useState(0);
  const listeners = useRef(new Set<StreamListener>());

  useEffect(() => {
    if (status !== 'active') {
      setConnection('closed');
      return;
    }

    const controller = new AbortController();
    void openGuestStream({
      client,
      signal: controller.signal,
      onStateChange: setConnection,
      onReset: () => setResetNonce((n) => n + 1),
      onEvent: (event) => {
        for (const listener of listeners.current) {
          // One listener throwing must not stop the rest of the page updating.
          try {
            listener(event);
          } catch {
            /* a subscriber's problem, not the stream's */
          }
        }
      },
    });

    return () => controller.abort();
  }, [status, client]);

  const value = useMemo<GuestStreamValue>(
    () => ({
      connection,
      resetNonce,
      stale: connection !== 'open',
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
    }),
    [connection, resetNonce],
  );

  return (
    <GuestStreamContext.Provider value={value}>{children}</GuestStreamContext.Provider>
  );
}

export function useGuestStream(): GuestStreamValue {
  const value = useContext(GuestStreamContext);
  if (!value) {
    throw new Error('useGuestStream must be used inside <GuestStreamProvider>');
  }
  return value;
}

/** Subscribe to stream frames for the life of a component. */
export function useStreamEvents(listener: StreamListener): void {
  const {subscribe} = useGuestStream();
  const ref = useRef(listener);
  ref.current = listener;
  useEffect(() => subscribe((event) => ref.current(event)), [subscribe]);
}
